import { useState } from 'react'
import { Field, Row, Tags, Text } from '../components/Form'
import Icon from '../components/Icon'
import type { IconName } from '../components/Icon'
import { resolvedAccessLevel } from '../types/models'
import { toDateInputValue } from '../lib/zonedTime'
import type { CrewDraft } from '../lib/mutations'
import type { TourCrew, WithId } from '../types/models'

/* ============================================================================
   The crew form, once.

   Used by `CrewDialog` (edit, in a modal) and by the New Tour flow (create,
   in the page). Extracted rather than copied for the same reason `DayItinerary`
   was: two copies of a form drift, and the one nobody is looking at is the one
   that stops matching the mutation it writes.
   ============================================================================ */

export function CrewFields({
  draft,
  patch,
  isNew,
  autoFocus = true,
}: {
  draft: CrewDraft
  patch: (next: Partial<CrewDraft>) => void
  /** Changes the hints only — a new member's email is where the invite goes. */
  isNew: boolean
  /** Off below the fold — see the note on `ShowFields`. */
  autoFocus?: boolean
}) {
  return (
    <>
      <Field label="Name">
        <Text value={draft.name} onChange={(value) => patch({ name: value })} autoFocus={autoFocus} />
      </Field>

      <Row>
        <Field label="Email" hint={isNew ? 'Where the invite goes.' : undefined}>
          <Text type="email" value={draft.email} onChange={(value) => patch({ email: value })} />
        </Field>
        <Field label="Phone">
          <Text value={draft.phone} onChange={(value) => patch({ phone: value })} />
        </Field>
      </Row>

      <Field label="Roles">
        <Tags value={draft.roles} onChange={(value) => patch({ roles: value })} />
      </Field>

      <AccessPicker draft={draft} patch={patch} />
    </>
  )
}

/**
 * Editor / Viewer, and the optional date window.
 *
 * Cards rather than a dropdown because each option needs a sentence to be
 * understood — "Viewer" alone doesn't tell anyone what a viewer can't do, and
 * a hint that only describes the *selected* option can't be compared against
 * the one you didn't pick.
 *
 * The date window is behind a checkbox. Two always-visible date fields on
 * every crew member read as required, and most crew are on for the whole run —
 * asking everyone to leave two fields blank to mean "the default" is backwards.
 */
function AccessPicker({
  draft,
  patch,
}: {
  draft: CrewDraft
  patch: (next: Partial<CrewDraft>) => void
}) {
  // Seeded from the draft rather than stored on the document: a member who
  // already has dates is limited by definition. Kept in local state so ticking
  // the box can reveal two empty date fields without writing anything, and
  // unticking can clear them.
  const [limited, setLimited] = useState(Boolean(draft.startDate || draft.endDate))

  return (
    <div className="stack" style={{ gap: 8 }}>
      <span className="t-eyebrow">Access</span>

      <OptionCard
        icon="edit"
        title="Editor"
        detail="Can change anything on the tour — schedule, shows, travel, crew and production."
        selected={draft.accessLevel === 'editor'}
        onSelect={() => patch({ accessLevel: 'editor' })}
      />
      <OptionCard
        icon="account"
        title="Viewer"
        detail="Sees the tour and everything they're on, but can't change it."
        selected={draft.accessLevel === 'viewer'}
        onSelect={() => patch({ accessLevel: 'viewer' })}
      />

      <OptionCard
        checkbox
        title="Only for part of the tour"
        detail="They'll see a countdown until their first day, and lose access after their last."
        selected={limited}
        onSelect={() => {
          // Unticking clears the dates. Leaving one behind would send someone
          // an access window they thought they had removed.
          if (limited) patch({ startDate: '', endDate: '' })
          setLimited(!limited)
        }}
      />

      {limited && (
        <Row>
          <Field label="Access from">
            <Text
              type="date"
              value={draft.startDate ?? ''}
              onChange={(value) => patch({ startDate: value })}
            />
          </Field>
          <Field label="Access until">
            <Text
              type="date"
              value={draft.endDate ?? ''}
              onChange={(value) => patch({ endDate: value })}
            />
          </Field>
        </Row>
      )}
    </div>
  )
}

function OptionCard({
  icon,
  title,
  detail,
  selected,
  checkbox,
  onSelect,
}: {
  icon?: IconName
  title: string
  detail: string
  selected: boolean
  checkbox?: boolean
  onSelect: () => void
}) {
  return (
    <button
      type="button"
      role={checkbox ? 'checkbox' : 'radio'}
      aria-checked={selected}
      onClick={onSelect}
      className="row"
      style={{
        gap: 12,
        width: '100%',
        textAlign: 'left',
        alignItems: 'flex-start',
        padding: '12px 14px',
        cursor: 'pointer',
        borderRadius: 'var(--radius-control)',
        background: selected ? 'var(--accent-wash)' : 'var(--panel-raised)',
        border: `${selected ? '2px' : 'var(--hairline-width)'} solid ${
          selected ? 'var(--accent)' : 'var(--hairline)'
        }`,
      }}
    >
      <span
        aria-hidden
        style={{
          flex: 'none',
          marginTop: 2,
          width: 16,
          height: 16,
          borderRadius: checkbox ? 4 : '50%',
          border: `2px solid ${selected ? 'var(--accent)' : 'var(--text-tertiary)'}`,
          background: selected ? 'var(--accent)' : 'transparent',
          boxShadow: selected && !checkbox ? 'inset 0 0 0 3px var(--panel)' : 'none',
        }}
      />
      <span className="stack" style={{ gap: 2, minWidth: 0 }}>
        <span className="row" style={{ gap: 7 }}>
          {icon && <Icon name={icon} size={14} style={{ color: 'var(--text-secondary)' }} />}
          <span style={{ fontWeight: 600 }}>{title}</span>
        </span>
        <span className="t-caption">{detail}</span>
      </span>
    </button>
  )
}

export function initialCrewDraft(member?: WithId<TourCrew> | null): CrewDraft {
  if (!member) {
    return { name: '', email: '', phone: '', roles: [], accessLevel: 'viewer' }
  }
  return {
    name: member.name,
    email: member.email ?? '',
    phone: member.phone ?? '',
    roles: member.roles ?? [],
    accessLevel: resolvedAccessLevel(member),
    startDate: member.startDate ? toDateInputValue(member.startDate.toDate()) : '',
    endDate: member.endDate ? toDateInputValue(member.endDate.toDate()) : '',
  }
}

/**
 * The joining code, shown after an invite.
 *
 * Never dismissed automatically — the code is the whole point of the screen,
 * and a toast that fades takes it away before it can be read out.
 */
export function InviteResult({
  name,
  email,
  tourName,
  joiningCode,
  existing,
}: {
  name: string
  email: string
  tourName: string
  joiningCode: string | null
  existing: boolean
}) {
  if (existing) {
    return (
      <p className="t-body">
        <strong>{name}</strong> already has an Encore account, so they've been added to {tourName}{' '}
        and notified in the app{email ? <> and by email at <strong>{email}</strong></> : null}.
      </p>
    )
  }

  return (
    <div className="stack" style={{ gap: 12 }}>
      <p className="t-body">
        {email ? (
          <>
            We've emailed <strong>{email}</strong> a joining code. They create an account with that
            address and enter the code.
          </>
        ) : (
          <>
            <strong>{name}</strong> has no email on file, so there was nothing to send to. Read them
            the code below — they create an account and enter it.
          </>
        )}
      </p>
      <div
        className="stack"
        style={{
          gap: 6,
          padding: 16,
          borderRadius: 'var(--radius-control)',
          background: 'var(--accent-wash)',
          alignItems: 'center',
        }}
      >
        <span className="t-eyebrow">Joining code</span>
        <span
          className="t-spec"
          style={{ fontSize: 28, letterSpacing: 6, color: 'var(--accent-text)' }}
        >
          {joiningCode}
        </span>
      </div>
      {email && (
        <p className="t-caption">
          Queued for delivery — a successful save means queued, not delivered. If the email doesn't
          arrive, read them the code.
        </p>
      )}
    </div>
  )
}
